/**
 * Reading a file as lines without ever holding it whole.
 *
 * This is the half of the streamed import that runs in the browser, and it is
 * deliberately the only part of parsing that does: the client splits bytes into
 * lines and stops there, and the server splits those lines into cells with the
 * same `splitLine` the preview used. If this file and `packages/core/src/csv.ts`
 * ever disagreed about where a line ends, every row after the disagreement
 * would be attributed to the wrong row number — so the rules here are the ones
 * `parseTable` follows, and nothing more.
 *
 * It is kept apart from the view so it can be exercised outside a browser,
 * where the interesting failures live: a character split across a chunk
 * boundary, a CRLF split across one, and a byte-order mark on the first cell.
 */

export interface Chunk {
  text: string;
  /** Bytes this chunk weighed on disk, for progress. Zero for a decoder flush. */
  bytes: number;
}

/** One mebibyte: large enough that the read is not syscall-bound, small enough
 *  that progress moves and an abort is noticed promptly. */
export const DEFAULT_CHUNK = 1 << 20;

/**
 * Decoded text out, one chunk at a time. `TextDecoder` in streaming mode is
 * what makes a character split across a chunk boundary survive; decoding each
 * chunk on its own would mangle every accented name landing on a seam.
 */
export async function* byteChunks(
  file: Blob, signal?: AbortSignal, chunkSize = DEFAULT_CHUNK,
): AsyncGenerator<Chunk> {
  const decoder = new TextDecoder('utf-8');
  const source = typeof file.stream === 'function'
    ? readStreamed(file, signal)
    : readSliced(file, signal, chunkSize);

  for await (const bytes of source) {
    yield { text: decoder.decode(bytes, { stream: true }), bytes: bytes.byteLength };
  }
  // Flushes whatever the decoder held back at the end of the last chunk.
  const tail = decoder.decode();
  if (tail) yield { text: tail, bytes: 0 };
}

async function* readStreamed(file: Blob, signal?: AbortSignal): AsyncGenerator<Uint8Array> {
  const reader = file.stream().getReader();
  try {
    for (;;) {
      if (signal?.aborted) return;
      const { done, value } = await reader.read();
      if (done) return;
      yield value as Uint8Array;
    }
  } finally {
    // Releases the underlying file handle when the consumer stops early, which
    // the preview always does — it reads a thousand rows and walks away.
    try { await reader.cancel(); } catch { /* already closed */ }
  }
}

/** For browsers without `Blob.stream()` — Safari before 14.1, mostly. */
async function* readSliced(file: Blob, signal: AbortSignal | undefined, chunkSize: number): AsyncGenerator<Uint8Array> {
  for (let at = 0; at < file.size; at += chunkSize) {
    if (signal?.aborted) return;
    const slice = file.slice(at, at + chunkSize);
    const buf = typeof slice.arrayBuffer === 'function'
      ? await slice.arrayBuffer()
      : await readViaFileReader(slice);
    yield new Uint8Array(buf);
  }
}

/** The last resort, for a browser with neither `stream()` nor `arrayBuffer()`. */
function readViaFileReader(blob: Blob): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error('That file could not be read.'));
    reader.readAsArrayBuffer(blob);
  });
}

/**
 * Chunks in, lines out. Line endings are the three `parseTable` accepts, and a
 * leading byte-order mark is dropped for the same reason it drops one: left on,
 * it becomes an invisible character on the first header cell and the column
 * stops matching its alias.
 *
 * Blank lines are yielded rather than filtered, because a caller counting rows
 * has to make that decision itself — the importer skips them exactly where
 * `parseTable` does.
 */
export async function* linesOf(
  chunks: AsyncIterable<Chunk>, onBytes?: (n: number) => void, signal?: AbortSignal,
): AsyncGenerator<string> {
  let carry = '';
  let first = true;

  for await (const chunk of chunks) {
    if (chunk.bytes > 0) onBytes?.(chunk.bytes);
    let text = chunk.text;
    if (first) { text = text.replace(/^﻿/, ''); first = false; }
    carry += text;

    // A CRLF straddling a chunk boundary would otherwise split into a phantom
    // blank line, so a trailing carriage return waits for its partner.
    const upto = carry.endsWith('\r') ? carry.length - 1 : carry.length;
    const parts = carry.slice(0, upto).split(/\r\n|\n|\r/);
    carry = (parts.pop() ?? '') + (upto < carry.length ? '\r' : '');
    for (const part of parts) {
      // Checked per line, not per chunk: one chunk can hold tens of thousands
      // of rows, and an abort that waited for the chunk to drain would not feel
      // like stopping at all.
      if (signal?.aborted) return;
      yield part;
    }
  }
  if (!signal?.aborted && carry.length > 0) yield carry;
}

/** The two above, joined: a file as a stream of lines. */
export function fileLines(
  file: Blob, onBytes?: (n: number) => void, signal?: AbortSignal, chunkSize = DEFAULT_CHUNK,
): AsyncGenerator<string> {
  return linesOf(byteChunks(file, signal, chunkSize), onBytes, signal);
}
